import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { DataTestType } from '@y42/data-test';
import { OperationInputColumn, OperationInputTypeToColumnTypes } from '@y42/datasource/shared/operation-input';
import { ColumnTypeEnum } from '@y42/json-schema';
import { isNullish } from '@y42/util/nullish';
import { combineLatest, Observable, startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataTestBuilderFormService } from '../../data-test-builder-form.service';
import { DataTestBuilderService } from '../../data-test-builder.service';

interface ColumnSelectUIModel {
  column: string;
  type: ColumnTypeEnum | null;
}

@UntilDestroy()
@Component({
  selector: 'y42-column-test',
  templateUrl: './column-test.component.html',
  styleUrls: ['./column-test.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnTestComponent {
  constructor(
    private readonly dataTestBuilderFormService: DataTestBuilderFormService,
    private readonly dataTestBuilderService: DataTestBuilderService,
  ) {}

  private readonly COLUMN_TYPE_FALLBACK = ColumnTypeEnum.Dynamic;

  readonly formGroup = this.dataTestBuilderFormService.getFormByTestType(DataTestType.Column);
  readonly visibilityConfig = {
    fieldName: false,
    applyButton: false,
    resetButton: false,
  };
  readonly thresholdFormArguments = {
    rowsFailedByConditionControl: this.formGroup.controls.rows_failed_by_condition,
    rowsFailedConditionControl: this.formGroup.controls.rows_failed_condition,
  };

  readonly trackByColumn = getTrackByFn<ColumnSelectUIModel>('column');

  private readonly columnFormControlValue$ = this.formGroup.controls.column.valueChanges.pipe(
    startWith(this.formGroup.controls.column.value),
  );

  private readonly supportedDataTypes = Object.values(OperationInputTypeToColumnTypes).flat();

  private readonly columns$: Observable<ColumnSelectUIModel[]> = combineLatest([
    this.dataTestBuilderService.allSchemaTestColumns$.pipe(
      map((allColumns) => allColumns.filter((column) => this.supportedDataTypes.includes(column.type))),
    ),
    this.columnFormControlValue$,
  ]).pipe(
    map(([schemaTestColumns, formControlValue]) => {
      const allColumns: ColumnSelectUIModel[] = schemaTestColumns.map(
        (item): { column: string; type: ColumnTypeEnum } => ({
          column: item.column,
          type: item.type,
        }),
      );

      // in case the column was deleted from the schema test
      if (formControlValue && !allColumns.find((x) => x.column === formControlValue)) {
        allColumns.push({ column: formControlValue, type: null });
      }

      return allColumns;
    }),
  );

  readonly state = connectState(this, {
    columns: this.columns$,
    selectedColumnType: combineLatest([this.columnFormControlValue$, this.columns$]).pipe(
      map(([columnName, columns]) => columns.find((x) => x.column === columnName)?.type ?? null),
    ),
    columnForOperationInput: combineLatest([this.columnFormControlValue$, this.columns$]).pipe(
      map(([columnName, columns]): OperationInputColumn | null => {
        if (isNullish(columnName) || columnName.trim() === '') {
          return null;
        }
        return {
          data_type: columns.find((c) => c.column === columnName)?.type ?? this.COLUMN_TYPE_FALLBACK,
          description: null,
          field_name: columnName,
        };
      }),
    ),
    isRelative: this.formGroup.controls.rows_failed_condition.valueChanges.pipe(
      startWith(this.formGroup.controls.rows_failed_condition.value),
      map((x) => x === 'relative'),
    ),
  });
}
